import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { AppointmentTypes, LocationTypes } from 'common';

import {
  GetInstructorsError,
  IGetInstructorsWebAdapter,
} from 'application/modules/bookingWizard/useCases/hooks/useCaseSelectInstructorWeb';
import mboAPI from 'infrastructure/redux/adapters/mboAPI/mboApi';
import {
  selectExperienceType,
  selectInstructorDetails,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import {
  setExperienceType,
  setInstructor,
  setInstructorDetails,
  setLocation,
  setLocationDetails,
  setPartySize,
  setReservationDate,
} from 'infrastructure/redux/slices/bookingWizard.slice';
import { selectUserSummary } from 'infrastructure/redux/slices/user.selector';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
function getFetchInstructorsError(
  error: FetchBaseQueryError | SerializedError | undefined,
): GetInstructorsError | undefined {
  if (!error) {
    return undefined;
  }
  return GetInstructorsError.Unknown;
}

const useGetInstructorsWeb: IGetInstructorsWebAdapter = () => {
  const dispatch = useAppDispatch();
  const flowType = useAppSelector(selectExperienceType);
  const selectedInstructor = useAppSelector(selectInstructorDetails);
  const { email } = useAppSelector(selectUserSummary);

  const [
    getInstructors,
    {
      data: instructors,
      isLoading: areInstructorsLoading,
      error: instructorsError,
      isFetching: areInstructorsFetching,
    },
  ] = mboAPI.useLazyGetAvailableStaffAppointmentsQuery();

  const [
    getInstructorsByDate,
    {
      data: availableInstructorsOnDate,
      currentData: currentAvailableInstructorsOnDate,
      isLoading: areAvailableInstructorsOnDateLoading,
      error: availableInstructorsOnDateError,
      isFetching: areAvailableInstructorsOnDateFetching,
    },
  ] = mboAPI.useLazyGetAvailableLessonAppointmentsQuery();
  const visibleAvailableInstructorsOnDate =
    currentAvailableInstructorsOnDate ?? availableInstructorsOnDate;

  const setFlowTypeToLesson = () => {
    dispatch(setExperienceType({ experienceType: AppointmentTypes.BookingWizardFlowTypes.Lesson }));
  };

  const clearPartySize = () => {
    dispatch(setPartySize({ partySize: undefined }));
  };

  const selectFirstLessonFlow = () => {
    dispatch(
      setExperienceType({ experienceType: AppointmentTypes.BookingWizardFlowTypes.FirstLesson }),
    );
    dispatch(setInstructor({ instructor: undefined }));
    dispatch(setInstructorDetails(undefined));
  };

  const saveSelectedInstructorToStore = (staffId: number) => {
    dispatch(setInstructor({ instructor: staffId }));
    // Try to find instructor details in both data sources
    let instructorDetails = instructors?.find((instructor) => instructor.staffId === staffId);
    if (!instructorDetails) {
      instructorDetails = visibleAvailableInstructorsOnDate?.find(
        (instructor) => instructor.staffId === staffId,
      );
    }
    dispatch(setInstructorDetails(instructorDetails));
  };

  const handleSaveSelectedLocationToStore = (selectedLocation: LocationTypes.SimulatorLocation) => {
    dispatch(setLocationDetails(selectedLocation));
    dispatch(setLocation(selectedLocation.id));
  };

  const handleSaveReservationDateToStore = (selectedReservationDate: Date) => {
    dispatch(setReservationDate({ reservationDate: selectedReservationDate }));
  };

  return {
    result: instructors,
    inProgress: areInstructorsLoading || areInstructorsFetching,
    error: getFetchInstructorsError(instructorsError),
    saveSelectedInstructorToStore,
    selectedInstructor,
    setFlowTypeToLesson,
    selectFirstLessonFlow,
    clearPartySize,
    getInstructors,
    flowType,
    handleSaveSelectedLocationToStore,
    handleSaveReservationDateToStore,
    userEmail: email,
    getInstructorsByDate,
    availableInstructorsOnDate: visibleAvailableInstructorsOnDate,
    areAvailableInstructorsOnDateLoading,
    availableInstructorsOnDateError,
    areAvailableInstructorsOnDateFetching,
  };
};

export default useGetInstructorsWeb;
